<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
	<v-card class="ma-2 mb-4">
		<v-toolbar :color="color" dark>
			<v-toolbar-title style="white-space: normal; display: flex; align-items: center;">
				<v-icon class="mr-3">mail</v-icon>
				{{ header }}
			</v-toolbar-title>
			<v-spacer/>
		</v-toolbar>
		<add-project-popup v-if="placingProjectAllowed">
		</add-project-popup>

		<v-alert :value="projects !== null && projects.length === 0" type="info">
			{{ emptyWarning }}
		</v-alert>

		<v-list three-line v-if="projects !== null">
			<template v-for="(project) in paginatedProjects">
				<v-list-tile :key="project.id" avatar>
					<v-list-tile-avatar>
						<div v-if="warningCodes.includes(project.agencyProjectCode)">
							<v-tooltip top>
								<template v-slot:activator="{ on }">
									<v-badge color="red" left v-on="on">
										<template>
											<span>!</span>
										</template>
										<v-icon
											:color="getProjectStatusColor(project)"
											:title="getProjectStatusMessage(project)">
											{{getProjectStatusIcon(project)}}
										</v-icon>
									</v-badge>
								</template>
							</v-tooltip>
						</div>
						<div v-else>
							<v-icon
								:color="getProjectStatusColor(project)"
								:title="getProjectStatusMessage(project)">
								{{getProjectStatusIcon(project)}}
							</v-icon>
						</div>
					</v-list-tile-avatar>

					<v-list-tile-content>
						<v-list-tile-title v-html="getAuthor(project)" v-if="agencyNameVisible"
							style="font-size: 14px"></v-list-tile-title>
						<v-list-tile-title v-html="getTileTitle(project)" style="font-size: 14px"></v-list-tile-title>
						<v-list-tile-sub-title
							v-html="getAgencyProjectCodeAndSupervisor(project)"></v-list-tile-sub-title>
						<v-list-tile-action-text style="display: flex; align-items: center;">
							<span v-html="getProjectStatusMessage(project)"></span>
							<egranty-icon-button v-if="project.correctionDeadline"
								icon="info"
								:label="'Termin poprawy: ' + project.correctionDeadline"
								class="custom-info-icon"
							/>
						</v-list-tile-action-text>
						<v-list-tile-action-text v-html="getEvaluationStats(project)" v-if="showEvaluationStats"
							style="font-weight: bold"></v-list-tile-action-text>
					</v-list-tile-content>

					<v-list-tile-action v-if="editingVisible && projectNotApproved(project) && !project.hideFromUser">
						<egranty-icon-button icon="edit"
							:color="color"
							label="Edycja"
							for="agency-projects-list-edition"
							id="agency-projects-list-edition"
							@click="editProject(project.id)" :disabled="!project.editAllowed"/>
					</v-list-tile-action>

					<slot name="extraAction" v-bind:project="project"/>

					<v-list-tile-action v-if="!project.hideFromUser">
						<egranty-icon-button icon="history" color="accent" label="Historia"
							@click="viewHistory(project.id)"/>
					</v-list-tile-action>

					<v-list-tile-action v-if="!project.hideFromUser">
						<egranty-icon-button icon="description"
							:color="color"
							label="Podgląd"
							for="agency-projects-list-preview"
							id="agency-projects-list-preview"
							@click="viewProject(project)"/>
					</v-list-tile-action>

					<v-list-tile-action v-if="roles.isAgencyEmployee() && !project.hideFromUser">
						<egranty-icon-button icon="delete"
							color="lightred"
							label="Usuń"
							for="agency-projects-list-delete"
							id="agency-projects-list-delete"
							:disabled="project.agencyProjectStatus !== 'NEW'"
							@click="deleteProject(project.id)"/>
					</v-list-tile-action>

					<v-list-tile-action v-if="isExpandable && !project.hideFromUser">
						<egranty-icon-button
							:icon="tileExpanded[project.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
							color="gray" :label="expandableAreaLabel"
							@click="expandTile(project.id)"
							:disabled="expansionDisabled(project)"/>
					</v-list-tile-action>
				</v-list-tile>

				<v-slide-y-transition :key="'more-' + project.id" v-if="isExpandable">
					<v-card flat v-show="tileExpanded[project.id]">
						<slot name="expandableArea" v-bind:project="project"/>
					</v-card>
				</v-slide-y-transition>
			</template>
		</v-list>
		<div class="text-xs-center">
			<v-pagination
				v-if="isPaginated"
				v-model="pagination.page"
				:length="length"
				:total-visible="7"
			></v-pagination>
		</div>

	</v-card>
</template>

<script>
	import {Dates} from "@/utils/Dates";
	import EgrantyIconButton from "../../../../genericComponents/EgrantyIconButton";
	import AddProjectPopup from "./../../agencyContest/AddProjectPopup";
	import {AgencyProject} from "@/api/generated/AgencyProject";
	import {Api} from "@/api";

	export default {
		name: "AgencyProjectsList",

		components: {AddProjectPopup, EgrantyIconButton},

		data() {
			return {
				tileExpanded: {},

				pagination: {
					page: 1,
					sortBy: null,
					projectsPerPage: 10,
				},
				userDomainRole: undefined,
			};
		},

		props: {
			projects: Array,
			contestId: String,
			header: String,
			emptyWarning: String,
			color: {type: String, default: "teal"},
			placingProjectAllowed: {type: Boolean, default: false},
			agencyNameVisible: {type: Boolean, default: false},
			editingVisible: {type: Boolean, default: false},
			isExpandable: {type: Boolean, default: false},
			isPaginated: {type: Boolean, default: false},
			expandableAreaLabel: {type: String, default: "Rozwiń..."},
			expansionDisabled: {
				type: Function, default: () => {
				},
			},
			inclusionStatuses: {
				type: Object, default: () => {
					return {
						expertEvaluationSchemaIncluded: false,
						finalEvaluationSchemaIncluded: false,
						individualEvaluationSchemaIncluded: false,
					};
				},
			},
			showEvaluationStats: {type: Boolean, default: false},
			warningCodes: {type: Array, default: () => []},
			search: {
				type: Object,
				default: () => {
					return {};
				},
			},
		},

		mixins: [
			Dates,
		],

		watch: {
			filteredProjects() {
				this.pagination.page = 1;
			}
		},

		computed: {
			length() {
				if (this.projects) {
					return Math.ceil(this.filteredProjects.length / this.pagination.projectsPerPage);
				} else {
					return 1;
				}
			},

			filteredProjects() {
				if (!this.isExpandable) {
					return this.projects;
				}
				let projects = this.projects;
				if (this.search.countryId) {
					projects = projects.filter(project => project.countryId === this.search.countryId);
				}

				if (this.search.projectCode) {
					projects = projects.filter(project => project.agencyProjectCode.includes(this.search.projectCode));
				}

				if (this.search.evaluating) {
					projects = projects.filter(project => {
						let contains = false;
						let se = project.substantiveEvaluations;
						let fie = project.finalEvaluation;
						let ee = project.expertEvaluations;
						se.forEach(e => {
							let user = e.teamUser.user.user;
							contains = contains | this.compareUser(user);
						});

						ee.forEach(e => {
							let user = e.agencyContestUser.user;
							contains = contains | this.compareUser(user);
						});
						if (fie) {
							let user = fie.teamUser.user.user;
							contains = contains | this.compareUser(user);
						}
						return contains;
					});
				}

				if (this.search.teamId) {
					if (this.search.teamId === -1) {
						projects = projects.filter(project => project.teamId === null);
					} else {
						projects = projects.filter(project => project.teamId === this.search.teamId);
					}
				}
				if (projects) {
					projects.sort((a, b) => {
						return this.parseProjectsOrdinalNumber(b.agencyProjectCode) - this.parseProjectsOrdinalNumber(a.agencyProjectCode);
					});
				}
				return projects;
			},

			paginatedProjects() {
				let firstProjectIndex = (this.pagination.page - 1) * this.pagination.projectsPerPage;
				return this.filteredProjects.slice(firstProjectIndex, Math.min(firstProjectIndex + this.pagination.projectsPerPage, this.filteredProjects.length));
			},

			notPaginatedProjectsCodes () {
				return this.filteredProjects.map(project => project.agencyProjectCode);
			}
		},

		methods: {

			compareUser(user) {
				return (user.name.concat(" ").concat(user.surname)).toLowerCase().includes(this.search.evaluating.toLowerCase());
			},

			parseProjectsOrdinalNumber(projectCode) {
				return projectCode.split("/")[2];
			},

			getAuthor(project) {
				return project.author.name + " " + project.author.surname + " (" + project.agency.name + ")";
			},

			getAgencyProjectCodeAndSupervisor(project) {
				let string = project.agencyProjectCode;
				if (project.hasOwnProperty("supervisor") && project.supervisor !== null) {
					string += "  |  Opiekun: " + project.supervisor.name + " " + project.supervisor.surname;
				}
				return string;
			},

			expandTile(projectId) {
				this.$set(this.tileExpanded, projectId, !this.tileExpanded[projectId]);
			},

			getTileTitle(project) {
				return project.agencyProjectData.title;
			},

			getProjectStatusIcon(project) {
				if (this.editingVisible) {
					return project.editAllowed ? "lock_open" : "lock";
				} else {
					switch (project.agencyProjectStatus) {
						case "SUBMITTED":
							return "check";
						case "REJECTED":
							return "block";
						case "AWAITING_CORRECTION":
							return "build";
						case "APPROVED":
						case "PROJECT_CHANGES_REQUESTED":
						case "FINISHED":
            case "ANNULLED":
							return "business";
					}
				}
			},

			getProjectStatusMessage(project) {
				switch (project.agencyProjectStatus) {
					case "NEW":
						return "Wniosek w trakcie uzupełniania";
					case "SUBMITTED":
						return "Wniosek wysłany";
					case "REJECTED":
						return "Wniosek odrzucony: " + project.rejectCause;
					case "AWAITING_CORRECTION":
						return "Wniosek oczekuje na poprawki: " + project.correctionCause;
					case "PROJECT_CHANGES_REQUESTED":
						return "Projekt oczekuje na poprawki: " + project.correctionCause;
					case "APPROVED":
						return "Wniosek zaakceptowany";
					case "FINISHED":
						return "Projekt zakończony";
          case "ANNULLED":
            return "Projekt anulowany";
				}
			},

			getProjectStatusColor(project) {
				switch (project.agencyProjectStatus) {
					case "NEW":
						return "teal";
					case "SUBMITTED":
						return "green";
					case "REJECTED":
          case "ANNULLED":
						return "red";
					case "AWAITING_CORRECTION":
					case "PROJECT_CHANGES_REQUESTED":
						return "orange darken-3";
					case "APPROVED":
					case "FINISHED":
						return "blue";
				}
			},

			projectNotApproved (project) {
				return !['APPROVED', 'PROJECT_CHANGES_REQUESTED', 'FINISHED'].includes(project.agencyProjectStatus);
			},

			getEvaluationStats(project) {
				let stats = [];
				if (this.inclusionStatuses.individualEvaluationSchemaIncluded) {
					stats.push(this.getIndividualEvaluationStats(project.substantiveEvaluations));
				}
        if (this.inclusionStatuses.expertEvaluationSchemaIncluded) {
          stats.push(this.getExpertEvaluationStats(project.expertEvaluations));
        }
				if (this.inclusionStatuses.finalEvaluationSchemaIncluded) {
					stats.push(this.getFinalEvaluationStats(project.finalEvaluation));
				}
				return stats.length > 0 ? stats.join(", ") : "Formularze ocen nie zostały jeszcze załączone";
			},

			getIndividualEvaluationStats(evaluationList) {
				let finishedEvalLength = evaluationList.filter(e => e.agencySubstantiveEvaluationStatus === "FINISHED").length;
				let listLength = evaluationList.length;
				return "M: " + finishedEvalLength + "/" + listLength;
			},

			getFinalEvaluationStats(evaluation) {
				if (evaluation === null) return "K: 0/0";
				return evaluation.agencyFinalEvaluationStatus === "FINISHED" ? "K: 1/1" : "K: 0/1";
			},

			getExpertEvaluationStats(evaluationList) {
				let finishedEvalLength = evaluationList.filter(e => e.agencyExpertEvaluationStatus === "FINISHED").length;
				let listLength = evaluationList.length;
				return "E: " + finishedEvalLength + "/" + listLength;
			},

			editProject(projectId) {
				this.$router.push("/app/agency-contest/" + this.contestId + "/project/" + projectId);
			},

			viewProject (project) {
				this.$router.openInNewTab("/view/agency-contest/" + this.contestId + "/project/" + project.agency.id + "/" + project.id);
			},

			viewHistory (projectId) {
				this.$router.push("/app/agency-project/" + projectId + "/history");
			},

			deleteProject (projectId) {
				this.$confirm("Czy na pewno chcesz usunąć wniosek?", {
					color: "lightred",
					icon: "delete",
					title: "Usuwanie wniosku",
					width: 500,
				})
					.then(res => {
						if (res) {
							AgencyProject.delete(this, projectId, successResponse => {
									this.$emit("project-removed", projectId);
									this.$messagesBus.$emit("success", "Wniosek został pomyślnie usunięty", successResponse);
								},
								errorResponse => {
									this.$messagesBus.$emit("error", "Nie udało się usunąć wniosku", errorResponse);
								});
						}
					});
			},

			isFinalEvaluationFinished(project) {
				if (!project.hasOwnProperty("finalEvaluation") || project.finalEvaluation === null ||
					!project.finalEvaluation.hasOwnProperty("agencyFinalEvaluationStatus") ||
					project.finalEvaluation.agencyFinalEvaluationStatus === null) {
					return false;
				}
				return project.finalEvaluation.agencyFinalEvaluationStatus === "FINISHED";
			},
		},

		async created () {
			await Api.get(this, "/agency-contest/user-role?contestId=".concat(this.$route.params.id), success => {
				this.userDomainRole = success;
			});
		},
	};
</script>

<style scoped>
	.v-toolbar >>> .v-toolbar__content {
		min-height: 64px !important;
		height: auto !important;
	}

	.theme--dark .v-alert.v-alert {
		color: #000 !important;
	}

	.custom-info-icon >>> .v-btn {
		width: 16px !important;
		height: 16px !important;
		min-width: 16px !important;
		padding: 0 !important;
		margin: 0 4px !important;
	}

	.custom-info-icon >>> .v-btn .v-icon {
		font-size: 16px !important;
	}
</style>
